import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useAuthStatus } from "context/OAuth/AuthContext";
import { toast } from "react-toastify";
import { Eye, EyeOff, MailCheck } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { getLoginMarketingLinks } from "@constants/loginMarketingLinks";
import useAuthLang from "@hooks/useAuthLang";
import { authPath } from "utils/authLanguage";

const links = getLoginMarketingLinks();

const requestSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email("Invalid email address")
    .required("Email is required"),
});

const resetSchema = Yup.object({
  password: Yup.string()
    // .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { t } = useTranslation();
  const authLang = useAuthLang();
  const { requestPasswordReset, confirmPasswordReset } = useAuthStatus();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Success state toggle karne k liye
  const [isEmailSent, setIsEmailSent] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: token ? resetSchema : requestSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!token) {
          // Case 1: Request Reset Link
          const { ok, data } = await requestPasswordReset( values.email.trim());
          if (ok) {
            setIsEmailSent(true);
          } else {
            toast.error(data?.message || t("Failed to send reset link."));
          }
        } else {
          // Case 2: Set New Password
          const { ok, data } = await confirmPasswordReset({
            token,
            password: values.password,
          });
          if (ok) {
            toast.success(t("Password updated successfully!"));
            window.location.href = links.login || "/login";
          } else {
            toast.error(data?.message || t("Failed to reset password."));
          }
        }
      } catch (err) {
        console.error(err);
        toast.error(t("Something went wrong. Please try again."));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="min-h-[450px] flex flex-col justify-center">
          {isEmailSent ? (
            // ================= SUCCESS VIEW STATE =================
            <div className="text-center animate-fade-in">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-50 mb-4 text-[#15AAFC]">
                <MailCheck className="h-7 w-7 text-green-600" />
              </div>
              <h1 className="text-2xl sm:text-[30px] font-[600] text-[#181D27] mb-3">
                {t("Check your email")}
              </h1>
              <p className="text-[#535862] text-sm sm:text-base mb-8 leading-relaxed">
                {t("We have successfully sent a password reset link to")}{" "}
                <strong className="text-black">{formik.values.email}</strong>.{" "}
                {t("Please click that link to reset your password.")}
              </p>

              <button
                onClick={() => setIsEmailSent(false)}
                className="text-[#0066EE] font-[600] text-sm hover:underline"
              >
                {t("Didn't receive the email? Try again")}
              </button>
            </div>
          ) : (
            // ================= FORM INPUT VIEW STATE =================
            <>
              <h1 className="text-center text-2xl sm:text-[30px] font-[600] text-[#181D27] mb-2">
                {t("Reset Your Password")}
              </h1>
              <p className="text-center text-[#535862] text-sm sm:text-base mb-8">
                {t("Reset Your Password To Continue")}
              </p>

              <form onSubmit={formik.handleSubmit} className="space-y-5">
               
                  <div>
                    <label className="block text-sm text-[#414651] mb-1">
                      {t("Username or Email")}{" "}
                      <span className="text-red-500" aria-hidden>
                        *
                      </span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      placeholder={t("Enter your email")}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full px-3 py-3 border rounded-lg text-[#1B1F26] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#15AAFC] focus:border-[#15AAFC] ${
                        formik.touched.email && formik.errors.email
                          ? "border-red-500 ring-1 ring-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className="text-red-500 text-xs mt-1 font-medium">
                        {t(formik.errors.email)}
                      </div>
                    )}
                  </div>
                
                

                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="w-full py-3.5 rounded-xl text-white font-semibold text-base disabled:opacity-70 disabled:cursor-not-allowed transition shadow-md hover:shadow-lg"
                  style={{
                    background:
                      "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
                  }}
                >
                  {formik.isSubmitting ? (
                    <ButtonLoader />
                  ) : (
                    t("Submit")
                  )}
                </button>
              </form>

              <p className="text-center text-sm text-[#535862] mt-8">
                <Link
                  to={authPath("login", authLang)}
                  className="text-[#0066EE] font-[600]"
                >
                  {t("Back to log in")}
                </Link>
              </p>
            </>
          )}
    </div>
  );
};

export default ResetPassword;
